import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Search, Target, Share2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import Reveal from "../components/Reveal";
import { services, process, company } from "../data/mock";
import Seo from "../components/Seo";

const PageHero = ({ eyebrow, title, subtitle }) => (
  <section className="relative navy-gradient overflow-hidden">
    <div className="absolute inset-0 grid-bg opacity-20" />
    <div className="blob h-72 w-72 bg-blue-500 -top-10 right-10" />
    <div className="relative mx-auto max-w-7xl px-6 py-20 md:py-28 text-center">
      <Reveal>
        <p className="text-sm font-bold uppercase tracking-[0.24em] text-blue-300">{eyebrow}</p>
        <h1 className="mt-4 font-display text-4xl md:text-5xl font-extrabold text-white max-w-3xl mx-auto leading-tight">{title}</h1>
        <p className="mt-5 text-lg text-blue-100/80 max-w-2xl mx-auto">{subtitle}</p>
      </Reveal>
    </div>
  </section>
);

export { PageHero };

const Services = () => {
  return (
    <div className="overflow-hidden">
      <Seo title="Services — SEO, Google Ads, Social & Web" description="A unified platform of digital marketing services — SEO, Google Ads, Facebook & social ads, web design, content, and analytics — that work together to grow your business." />
      <PageHero
        eyebrow="Services"
        title="Everything you need to win online — under one roof"
        subtitle="A unified platform of digital marketing services that synergize to deliver unparalleled results for your business."
      />

      <section className="section-pad bg-white">
        <div className="mx-auto max-w-7xl px-6 space-y-24">
          {services.map((s, i) => (
            <Reveal key={s.slug}>
              <div className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${i % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""}`}>
                <div className="relative">
                  <div className="rounded-3xl overflow-hidden shadow-xl ring-1 ring-slate-200">
                    <img src={s.image} alt={s.title} className="w-full h-80 object-cover" />
                  </div>
                  <div className="absolute -bottom-5 -left-5 flex h-16 w-16 items-center justify-center rounded-2xl brand-gradient shadow-xl">
                    <s.icon className="h-8 w-8 text-white" />
                  </div>
                </div>
                <div>
                  <Badge className="bg-blue-50 text-blue-700 hover:bg-blue-50 border border-blue-100 font-semibold">Service {String(i + 1).padStart(2, "0")}</Badge>
                  <h2 className="mt-4 font-display text-3xl font-extrabold text-slate-900">{s.title}</h2>
                  <p className="mt-4 text-lg text-slate-600 leading-relaxed">{s.long}</p>
                  <div className="mt-6 grid sm:grid-cols-2 gap-3">
                    {s.points.map((pt) => (
                      <div key={pt} className="flex items-center gap-2.5 text-slate-700 font-medium">
                        <CheckCircle2 className="h-5 w-5 text-blue-600 shrink-0" /> {pt}
                      </div>
                    ))}
                  </div>
                  <a href={company.bookingUrl} target="_blank" rel="noreferrer">
                    <Button className="mt-8 brand-gradient text-white font-bold group">
                      Get started with {s.title}
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </a>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Process recap */}
      <section className="section-pad bg-slate-50">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal className="text-center max-w-2xl mx-auto">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-blue-600">Getting started</p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-extrabold text-slate-900">From hello to winning in 4 steps</h2>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {process.map((p, i) => (
              <Reveal key={p.step} delay={i * 80}>
                <div className="relative h-full rounded-2xl bg-white border border-slate-200 p-7 card-lift">
                  <span className="font-display text-5xl font-extrabold text-slate-100 absolute top-4 right-5 select-none">{p.step}</span>
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                    <p.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 font-display text-lg font-bold text-slate-900">{p.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="text-center mt-12">
            <a href={company.bookingUrl} target="_blank" rel="noreferrer">
              <Button size="lg" className="brand-gradient text-white font-bold text-base group">
                Schedule Your Free Call
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Button>
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Services;
